import Autocomplete from '@mui/material/Autocomplete';
import parse from 'autosuggest-highlight/parse';
import { Box, ButtonBase, Grid2, Paper, PaperProps, Stack, Typography, useTheme } from '@mui/material';
import { ControllerRenderProps, FieldError } from 'react-hook-form';
import { useId, useState } from 'react';
import { useGoogleAddressAutocomplete } from '../hooks/useGoogleAddressAutocomplete';
import { WarningAmberOutlined } from '@mui/icons-material';
import { APIProvider, Map } from '@vis.gl/react-google-maps';
import { TextField } from './TextField';
import { AddressInput, AddressValue } from '../types/FormField';
import { API_FIELDS_CONTACT } from '../configs';

const defaultInputs: AddressInput[] = [
  { name: API_FIELDS_CONTACT.addressLine1, label: 'Address Line 1', required: true },
  { name: API_FIELDS_CONTACT.city, label: 'City', required: true },
  { name: API_FIELDS_CONTACT.postalCode, label: 'Postal Code', required: true },
  { name: API_FIELDS_CONTACT.addressLine2, label: 'Address Line 2' },
  { name: API_FIELDS_CONTACT.country, label: 'Country', required: true },
  { name: API_FIELDS_CONTACT.state, label: 'State', required: true },
];

interface AddressAutocompleteFieldProps {
  googleMapsAPIKey: string;
  value: AddressValue;
  onChange: ControllerRenderProps['onChange'];
  disabled: boolean;
  name: string;
  label: string;
  readOnly?: boolean;
  restrictToUS?: boolean;
  error?: FieldError;
  addressInputs?: AddressInput[];
}

function CustomPaper(props: PaperProps) {
  return (
    <Paper
      {...props}
      elevation={5}
      sx={{
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
        border: '1px #AEAEAF',
        px: 2,
        py: 1,
        minWidth: 'max-content',
        boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
        '& .MuiAutocomplete-noOptions': {
          px: 2,
          py: 1,
        },
      }}
    >
      {props.children}
    </Paper>
  );
}

const AutoCompleteOption = ({ option }: { option: google.maps.places.AutocompletePrediction }) => {
  const matches = option.structured_formatting.main_text_matched_substrings;

  const parts: { text: string; highlight: boolean }[] = parse(
    option.structured_formatting.main_text,
    matches.map((match: { offset: number; length: number }) => [match.offset, match.offset + match.length]),
  );

  const key = useId();

  return (
    <Grid2 sx={{ wordWrap: 'break-word' }}>
      {parts.map((part) => (
        <Box
          key={`${key}${part.text}`}
          component="span"
          sx={{
            fontWeight: part.highlight ? 'fontWeightBold' : 'fontWeightRegular',
          }}
        >
          {part.text}
        </Box>
      ))}
      {option.structured_formatting.secondary_text ? (
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {option.structured_formatting.secondary_text}
        </Typography>
      ) : null}
    </Grid2>
  );
};

interface GoogleApiAutoCompleteProps {
  setManualMode: (isManualMode: boolean) => void;
  handlePlaceDetails: (details: google.maps.places.PlaceResult | null, prediction: google.maps.places.AutocompletePrediction | null) => void;
  name: string;
  label: string;
  restrictToUS?: boolean;
}

const GoogleApiAutoComplete = ({ setManualMode, handlePlaceDetails, name, label, restrictToUS }: GoogleApiAutoCompleteProps) => {
  const { predictionResults, onInputChange, handleSuggestionClick, selectedSuggestion, loading } = useGoogleAddressAutocomplete(
    handlePlaceDetails,
    restrictToUS,
  );
  const theme = useTheme();
  return (
    <>
      <div>
        {/* TODO: find a way to remove map */}
        <Map zoom={null} center={null} />
      </div>
      <Autocomplete
        fullWidth
        autoComplete
        includeInputInList
        filterSelectedOptions
        loading={loading}
        popupIcon={null}
        clearIcon={null}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
        slots={{
          paper: CustomPaper,
        }}
        options={predictionResults}
        value={selectedSuggestion}
        noOptionsText={
          <Grid2 container direction="row" display="flex" alignItems="flex-end" size={12}>
            {/* TODO: refactor once USPS is integrated */}
            <Typography variant="caption">
              Sorry, we cannot find that address in the USPS database.{' '}
              <ButtonBase sx={{ textDecoration: 'underline', textDecorationColor: theme.palette.primary.main }} onClick={() => setManualMode(true)}>
                <Typography variant="caption" color="primary">
                  Please enter it manually
                </Typography>
              </ButtonBase>
            </Typography>
            <WarningAmberOutlined sx={{ marginLeft: '0.4rem' }} color="warning" fontSize="small" />
          </Grid2>
        }
        onChange={(_event: React.SyntheticEvent<Element, Event>, newValue: google.maps.places.AutocompletePrediction | null) => {
          handleSuggestionClick(newValue);
        }}
        onInputChange={(_event: React.SyntheticEvent<Element, Event>, newInputValue) => {
          onInputChange(newInputValue);
        }}
        renderInput={(params) => {
          const { InputProps, inputProps } = params;
          return <TextField value={inputProps.value?.toString() ?? ''} slotProps={{ input: InputProps, htmlInput: inputProps }} label={label} name={name} />;
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              <Grid2 container sx={{ alignItems: 'center' }}>
                <AutoCompleteOption option={option} />
              </Grid2>
            </li>
          );
        }}
      />
    </>
  );
};

export const AddressAutocompleteField = ({
  googleMapsAPIKey,
  name,
  value,
  onChange,
  disabled,
  label,
  readOnly,
  restrictToUS,
  error,
  addressInputs = defaultInputs,
}: AddressAutocompleteFieldProps) => {
  const theme = useTheme();
  const [enterManually, setManualMode] = useState(false);

  const handleInputChange = (key: keyof AddressValue, val: string) => {
    const newValue: AddressValue = value || {
      [API_FIELDS_CONTACT.addressLine1]: '',
      [API_FIELDS_CONTACT.addressLine2]: '',
      [API_FIELDS_CONTACT.postalCode]: '',
      [API_FIELDS_CONTACT.city]: '',
      [API_FIELDS_CONTACT.state]: '',
      [API_FIELDS_CONTACT.country]: '',
    };

    newValue[key] = val;

    onChange(newValue);
  };

  const handlePlaceDetails = (details: google.maps.places.PlaceResult | null, prediction: google.maps.places.AutocompletePrediction | null) => {
    const newValue: AddressValue = {
      [API_FIELDS_CONTACT.addressLine1]: prediction?.structured_formatting.main_text ?? '',
      [API_FIELDS_CONTACT.addressLine2]:
        details?.address_components?.find((ac) => ac.types.includes('sublocality') || ac.types.includes('neighborhood'))?.long_name ?? '',
      [API_FIELDS_CONTACT.postalCode]: details?.address_components?.find((ac) => ac.types.includes('postal_code'))?.long_name ?? '',
      [API_FIELDS_CONTACT.city]: details?.address_components?.find((ac) => ac.types.includes('locality'))?.long_name ?? '',
      [API_FIELDS_CONTACT.state]: details?.address_components?.find((ac) => ac.types.includes('administrative_area_level_1'))?.long_name ?? '',
      [API_FIELDS_CONTACT.country]: details?.address_components?.find((ac) => ac.types.includes('country'))?.long_name ?? '',
    };

    onChange(newValue);
  };

  return (
    <Grid2 container wrap="wrap" spacing={{ xs: 0, md: 4 }}>
      <Grid2 size={{ xs: 12, md: 4 }}>
        <APIProvider apiKey={googleMapsAPIKey}>
          <GoogleApiAutoComplete label={label} name={name} setManualMode={setManualMode} handlePlaceDetails={handlePlaceDetails} restrictToUS={restrictToUS} />
        </APIProvider>
      </Grid2>

      <Grid2 size={{ xs: 12, md: 4 }} container sx={{ display: 'flex', alignItems: 'flex-end' }}>
        <ButtonBase onClick={() => setManualMode(true)} sx={{ textDecoration: 'underline', pb: '0.625rem', textDecorationColor: theme.palette.primary.main }}>
          <Typography sx={{}} color="primary" variant="subtitle1">
            Can't find your address? Enter it manually
          </Typography>
        </ButtonBase>
      </Grid2>

      <Grid2 size={{ xs: 0, md: 4 }} />

      {addressInputs.map(({ name: inputName, label: inputLabel, required: fieldRequired }) => {
        // Don't render country field if country is fixed to US and don't render state field if country is not fixed  to US.
        if (restrictToUS && inputName === API_FIELDS_CONTACT.country) return;
        if (!restrictToUS && inputName === API_FIELDS_CONTACT.state) return;

        const fieldError = error && fieldRequired && !value?.[inputName] ? { type: 'required', message: `${inputLabel} is required` } : undefined;
        const inputValue = value ? value[inputName] : '';

        return (
          <Grid2 size={{ xs: 12, md: 4 }} key={inputName}>
            <Stack gap={0.5}>
              <TextField
                name={`${name}-${inputName}`}
                label={inputLabel}
                disabled={disabled || !enterManually}
                value={inputValue}
                onChange={(inputValue) => handleInputChange(inputName, inputValue)}
                error={fieldError}
                readOnly={readOnly}
              />
            </Stack>
          </Grid2>
        );
      })}
    </Grid2>
  );
};
